import React, { useEffect, useState } from "react";
import {
    fetchInvoices,
    deleteInvoice,
    fetchInvoiceById,
} from "../../services/api/billing/invoiceService";
import { useNavigate } from "react-router-dom";
import { Eye, FileText, Trash2, Download } from "lucide-react";
import { generateInvoicePDF } from "./generateInvoicePDF";
import {
    getPaymentsByInvoice,
    createPayment,
    uploadPaymentAttachment,
    downloadPayment
} from "../../services/api/billing/paymentService";
import { Plus, X } from "lucide-react";
import toast from "react-hot-toast";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const BillingDashboard = () => {
    const [invoices, setInvoices] = useState([]);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [loading, setLoading] = useState(false);

    const [preview, setPreview] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();

    // payment
    const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
    const [payments, setPayments] = useState({});
    const [showPaymentForm, setShowPaymentForm] = useState({});

    const [paymentForm, setPaymentForm] = useState({});

    const [statusFilter, setStatusFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [stats, setStats] = useState({
        total: 0,
        paid: 0,
        pending: 0
    });

    // 🔁 Debounce
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 500);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        loadInvoices();
    }, [debouncedSearch, page]);

    const loadInvoices = async () => {
        setLoading(true);
        try {
            const res = await fetchInvoices({
                search: debouncedSearch,
                page,
                status: statusFilter,
                from_date: fromDate,
                to_date: toDate
            });

            const data = res.data || [];

            setInvoices(data);
            setMeta(res.meta || {});

            // ✅ CALCULATE STATS
            let total = 0, paid = 0, pending = 0;

            data.forEach(inv => {
                total += Number(inv.total);
                if (inv.status === "paid") paid += Number(inv.total);
                else pending += Number(inv.total);
            });

            setStats({ total, paid, pending });

        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const loadPayments = async (invoiceId) => {
        try {
            const res = await getPaymentsByInvoice(invoiceId);
            const data = res.data || [];

            setPayments(prev => ({
                ...prev,
                [invoiceId]: data
            }));
        } catch {
            toast.error("Failed to load payments");
        }
    };

    const togglePayments = (id) => {
        if (expandedInvoiceId === id) {
            setExpandedInvoiceId(null);
        } else {
            setExpandedInvoiceId(id);
            loadPayments(id);
        }
    };

    const togglePaymentForm = (id) => {
        setShowPaymentForm(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
        setPaymentForm(prev => ({
            ...prev,
            [id]: prev[id] || {
                amount: "",
                payment_date: "",
                mode: "upi",
                txn_ref_no: "",
                attachment: null,
            }
        }));
    };


    // ❌ Delete
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this invoice?")) return;

        try {
            await deleteInvoice(id);
            alert("Invoice deleted");
            loadInvoices();
        } catch (err) {
            alert("Delete failed");
        }
    };

    // 🎨 Status Badge
    const getStatusBadge = (status) => {
        if (status === "paid") {
            return (
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                    Paid
                </span>
            );
        }
        return (
            <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs">
                Pending
            </span>
        );
    };

    // 📥 PDF Download (innovice)
    const handleDownload = async (id) => {
        try {
            const data = await fetchInvoiceById(id);
            generateInvoicePDF(data);
        } catch (err) {
            console.error("PDF download error", err);
        }
    };

    const handleSavePayment = async (invoiceId) => {
        const form = paymentForm[invoiceId] || {};

        if (!form.amount || !form.payment_date) {
            return toast.error("Amount & Date required");
        }

        if ((form.mode === "upi" || form.mode === "bank") && !form.txn_ref_no) {
            return toast.error("Txn Ref required");
        }

        try {
            // ✅ STEP 1: Create Payment
            const res = await createPayment({
                invoice_id: invoiceId,
                amount: Number(form.amount),
                payment_date: form.payment_date,
                mode: form.mode,
                txn_ref_no: form.txn_ref_no
            });

            console.log("Create Payment Response:", res);

            // ✅ SAFE ID EXTRACTION (VERY IMPORTANT)
            const paymentId =
                res?.data?.data?.id ||
                res?.data?.id ||
                res?.id;

            if (!paymentId) {
                throw new Error("Payment ID not found");
            }

            console.log("Payment ID:", paymentId);

            // ✅ STEP 2: Upload File (if exists)
            if (form.attachment) {
                const fd = new FormData();
                fd.append("attachment", form.attachment);

                console.log("Uploading file:", form.attachment);

                await uploadPaymentAttachment(paymentId, fd);
            }

            toast.success("Payment added successfully");

            // ✅ Reset form
            setPaymentForm(prev => ({
                ...prev,
                [invoiceId]: {}
            }));

            // ✅ Close form
            setShowPaymentForm(prev => ({
                ...prev,
                [invoiceId]: false
            }));

            // ✅ Reload payments
            loadPayments(invoiceId);

        } catch (err) {
            console.error("Payment Error:", err?.response?.data || err.message);
            toast.error("Payment failed");
        }
    };

    const handlePaymentDownload = async (id) => {
        try {
            const res = await downloadPayment(id);
            const blob = new Blob([res.data]);
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `payment_${id}.pdf`;
            link.click();

            URL.revokeObjectURL(url);
        } catch {
            toast.error("Download failed");
        }
    };

    const renderPayments = (inv) => (
        <>
            <div className="flex justify-between mb-2">
                <h3 className="font-semibold text-sm">
                    Payments for #{inv.invoice_no}
                </h3>

                <button
                    onClick={() => togglePaymentForm(inv.id)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg shadow w-full md:w-auto"
                >
                    {showPaymentForm[inv.id] ? "Close" : "+ Add"}
                </button>
            </div>

            {showPaymentForm[inv.id] && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                    <input type="number" placeholder="Amount" className="border p-2"
                        onChange={(e) =>
                            setPaymentForm(prev => ({
                                ...prev,
                                [inv.id]: { ...prev[inv.id], amount: e.target.value }
                            }))
                        }
                    />

                    <input type="date" className="border p-2"
                        onChange={(e) =>
                            setPaymentForm(prev => ({
                                ...prev,
                                [inv.id]: { ...prev[inv.id], payment_date: e.target.value }
                            }))
                        }
                    />

                    <select className="border p-2"
                        onChange={(e) =>
                            setPaymentForm(prev => ({
                                ...prev,
                                [inv.id]: { ...prev[inv.id], mode: e.target.value }
                            }))
                        }
                    >
                        <option value="upi">UPI</option>
                        <option value="cash">Cash</option>
                        <option value="bank">Bank</option>
                    </select>

                    <input type="text" placeholder="Txn Ref" className="border p-2"
                        onChange={(e) =>
                            setPaymentForm(prev => ({
                                ...prev,
                                [inv.id]: { ...prev[inv.id], txn_ref_no: e.target.value }
                            }))
                        }
                    />

                    <input type="file"
                        onChange={(e) => {
                            const file = e.target.files[0];
                            setPaymentForm(prev => ({
                                ...prev,
                                [inv.id]: { ...prev[inv.id], attachment: file }
                            }));
                        }}
                    />

                    <button
                        onClick={() => handleSavePayment(inv.id)}
                        className="bg-green-600 text-white p-2 rounded col-span-2 md:col-span-1"
                    >
                        Save
                    </button>
                </div>
            )}

            <div className="mt-3">

                {/* 🖥️ DESKTOP TABLE */}
                <div className="hidden md:block overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 text-gray-600">
                            <tr>
                                <th className="p-2 text-left">ID</th>
                                <th className="p-2 text-left">Amount</th>
                                <th className="p-2 text-left">Date</th>
                                <th className="p-2 text-left">Mode</th>
                                <th className="p-2 text-left">Txn Ref</th>
                                <th className="p-2 text-center">Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {payments[inv.id]?.length > 0 ? (
                                payments[inv.id].map((p, index) => (
                                    <tr key={p.id} className="border-t hover:bg-gray-50">

                                        <td className="p-2">{index + 1}</td>

                                        <td className="p-2 font-semibold text-gray-800">
                                            ₹ {p.amount}
                                        </td>

                                        <td className="p-2">{p.payment_date}</td>

                                        <td className="p-2">
                                            <span className={`px-2 py-1 rounded text-xs font-medium
                                    ${p.mode === "upi" ? "bg-blue-100 text-blue-700" : ""}
                                    ${p.mode === "cash" ? "bg-green-100 text-green-700" : ""}
                                    ${p.mode === "bank" ? "bg-purple-100 text-purple-700" : ""}
                                `}>
                                                {p.mode.toUpperCase()}
                                            </span>
                                        </td>

                                        <td className="p-2 text-gray-600">
                                            {p.txn_ref_no || "-"}
                                        </td>

                                        <td className="p-2 text-center">
                                            <button
                                                onClick={() => handlePaymentDownload(p.id)}
                                                className="bg-gray-100 hover:bg-gray-200 p-2 rounded"
                                            >
                                                <Download size={14} />
                                            </button>
                                        </td>

                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center p-3 text-gray-500">
                                        No payments found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* 📱 MOBILE CARDS */}
                <div className="md:hidden space-y-2">
                    {payments[inv.id]?.length > 0 ? (
                        payments[inv.id].map(p => (
                            <div key={p.id} className="bg-white p-3 rounded-lg shadow-sm text-sm">

                                <div className="flex justify-between mb-1">
                                    <span className="font-semibold">₹ {p.amount}</span>

                                    <span className={`px-2 py-1 rounded text-xs
                            ${p.mode === "upi" ? "bg-blue-100 text-blue-700" : ""}
                            ${p.mode === "cash" ? "bg-green-100 text-green-700" : ""}
                            ${p.mode === "bank" ? "bg-purple-100 text-purple-700" : ""}
                        `}>
                                        {p.mode.toUpperCase()}
                                    </span>
                                </div>

                                <p className="text-gray-500 text-xs">
                                    {p.payment_date}
                                </p>

                                <p className="text-gray-600 text-xs">
                                    Txn: {p.txn_ref_no || "-"}
                                </p>

                                <div className="flex justify-end mt-2">
                                    <button
                                        onClick={() => handlePaymentDownload(p.id)}
                                        className="text-blue-600 text-xs"
                                    >
                                        Download
                                    </button>
                                </div>

                            </div>
                        ))
                    ) : (
                        <p className="text-center text-gray-500">No payments</p>
                    )}
                </div>

            </div>
        </>
    );

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">

            {/* HEADER */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Billing Dashboard</h2>
                    <p className="text-gray-500 text-sm">Manage invoices & payments</p>
                </div>

                <button
                    onClick={() => navigate(`/billing/create-invoice`)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg shadow w-full md:w-auto"
                >
                    + Create Invoice
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Total Revenue</p>
                    <h2 className="text-xl font-bold text-blue-600">₹ {stats.total.toFixed(2)}</h2>
                </div>

                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Paid</p>
                    <h2 className="text-xl font-bold text-green-600">₹ {stats.paid.toFixed(2)}</h2>
                </div>

                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Pending</p>
                    <h2 className="text-xl font-bold text-yellow-600">₹ {stats.pending.toFixed(2)}</h2>
                </div>

            </div>

            {/* SEARCH */}
            <div className="bg-white p-4 rounded-lg shadow mb-4 flex flex-col md:flex-row gap-3">

                {/* Search */}
                <input
                    type="text"
                    placeholder="Search invoice..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border p-2 rounded w-full md:w-1/4"
                />

                {/* Status */}
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border p-2 rounded"
                >
                    <option value="">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                </select>

                {/* Date Range */}
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border p-2 rounded" />
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border p-2 rounded" />

                {/* Reset */}
                <button
                    onClick={() => {
                        setSearch("");
                        setStatusFilter("");
                        setFromDate("");
                        setToDate("");
                    }}
                    className="bg-gray-200 px-4 rounded"
                >
                    Reset
                </button>

            </div>



            {/* DESKTOP TABLE */}
            <div className="hidden md:block bg-white shadow rounded-lg overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-100 text-gray-600 text-sm">
                        <tr className="text-left">
                            <th className="p-3 ">SlNo</th>
                            <th className="p-3 ">Invoice</th>
                            <th className="p-3">Date</th>
                            <th className="p-3">Total Amount</th>
                            <th className="p-3">Paid Amount</th>
                            <th className="p-3">Status</th>
                            <th className="p-3 text-center">Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {invoices.map((inv,index) => (
                            <React.Fragment key={inv.id}>
                                <tr className="border-t hover:bg-gray-50">
                                    <td className="p-3">{index + 1}</td>
                                    <td className="p-3 font-medium">{inv.invoice_no}</td>
                                    <td className="p-3">{inv.invoice_date}</td>
                                    <td className="p-3 font-semibold">₹ {Number(inv.total).toFixed(2)}</td>
                                    <td className="p-3 font-semibold">₹ {Number(inv.paid_amount).toFixed(2)}</td>
                                    <td className="p-3">{getStatusBadge(inv.status)}</td>

                                    <td className="p-3">
                                        <div className="flex justify-center gap-2">

                                            <button onClick={() => togglePayments(inv.id)} className="p-2 bg-purple-100 rounded">
                                                ₹
                                            </button>

                                            <button
                                                onClick={() => navigate(`/billing/invoice/${inv.id}`)}
                                                className="p-2 bg-gray-100 rounded"
                                            >
                                                <Eye size={16} />
                                            </button>

                                            <button onClick={() => handleDownload(inv.id)} className="p-2 bg-blue-100 rounded">
                                                <Download size={16} />
                                            </button>

                                            <button onClick={() => handleDelete(inv.id)} className="p-2 bg-red-100 rounded">
                                                <Trash2 size={16} />
                                            </button>

                                        </div>
                                    </td>
                                </tr>

                                {/* EXPANDED PAYMENT */}
                                {expandedInvoiceId === inv.id && (
                                    <tr>
                                        <td colSpan="7">
                                            <div className="p-4 bg-gray-50">
                                                {renderPayments(inv)}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 📱 MOBILE VIEW */}
            <div className="md:hidden space-y-4">
                {invoices.map(inv => (
                    <div key={inv.id} className="bg-white p-4 rounded-lg shadow">

                        <div className="flex justify-between items-center">
                            <h3 className="font-semibold">{inv.invoice_no}</h3>
                            {getStatusBadge(inv.status)}
                        </div>

                        <p className="text-sm text-gray-500">{inv.invoice_date}</p>

                        <p className="mt-2 font-bold text-lg">₹ {Number(inv.total).toFixed(2)}</p>

                        {/* ACTIONS */}
                        <div className="flex justify-between mt-3 text-gray-600">
                            <button className="flex flex-col items-center text-xs" onClick={() => togglePayments(inv.id)}>
                                ₹ <span>Pay</span>
                            </button>

                            <button
                                onClick={() => navigate(`/billing/invoice/${inv.id}`)}
                                className="flex flex-col items-center text-xs"
                            >
                                <Eye size={16} />
                            </button>

                            <button className="flex flex-col items-center text-xs" onClick={() => handleDownload(inv.id)}>
                                <Download size={16} />
                                <span>PDF</span>
                            </button>

                            <button className="flex flex-col items-center text-xs" onClick={() => handleDelete(inv.id)}>
                                <Trash2 size={16} />
                                <span>Delete</span>
                            </button>
                        </div>

                        {/* EXPANDED */}
                        {expandedInvoiceId === inv.id && (
                            <div className="mt-3 border-t pt-3">
                                {renderPayments(inv)}
                            </div>
                        )}

                    </div>
                ))}
            </div>

            {/* PAGINATION */}
            <div className="flex justify-between items-center mt-6">
                <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                    className="px-4 py-1 border rounded disabled:opacity-50"
                >
                    Prev
                </button>

                <span>Page {meta.current_page || page}</span>

                <button
                    disabled={!meta.next_page_url}
                    onClick={() => setPage(page + 1)}
                    className="px-4 py-1 border rounded disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default BillingDashboard;